import React from 'react';
import { cva } from 'class-variance-authority';
import PropTypes from 'prop-types';

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';

import cnMerge from '../../lib/utils';

const toggleGroupItemVariants = cva(
    [
        'inline-flex items-center justify-center rounded-md px-3 py-1.5',
        'text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow'
    ].join(' '),
    {
        variants: {
            variant: {
                default: 'bg-transparent text-muted-foreground hover:text-foreground'
            },
            size: {
                default: 'h-9',
                sm: 'h-8',
                lg: 'h-10'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const ToggleGroup = React.forwardRef(({ className, ...props }, ref) => (
    <ToggleGroupPrimitive.Root
        ref={ref}
        className={cnMerge(
            'inline-flex rounded-lg border border-border bg-muted p-1 text-muted-foreground',
            className
        )}
        {...props}
    />
));

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

ToggleGroup.propTypes = {
    className: PropTypes.string
};

ToggleGroup.defaultProps = {
    className: undefined
};

const ToggleGroupItem = React.forwardRef(({ className, variant, size, ...props }, ref) => (
    <ToggleGroupPrimitive.Item
        ref={ref}
        className={cnMerge(toggleGroupItemVariants({ variant, size, className }))}
        {...props}
    />
));

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

ToggleGroupItem.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default']),
    size: PropTypes.oneOf(['default', 'sm', 'lg'])
};

ToggleGroupItem.defaultProps = {
    className: undefined,
    variant: undefined,
    size: undefined
};

export { ToggleGroup, ToggleGroupItem };
